import React, { useState, useRef } from "react";
import { FormControl, MenuItem } from "@material-ui/core";
import EnhancedMaterialTable from "components/EnhancedMaterialTable/EnhancedMaterialTable";
import SetPointForm from "MetaComponent/containers/TargetCanvas/components/DesignTargetDetails/components/SetPoint/components/SetPointForm/SetPointForm";
import NumberInput from "components/NumberInput/NumberInput";
import UnselfishSelect from "components/UnselfishSelect/UnselfishSelect";
import Autocomplete from "components/Autocomplete";

export const unitOptions = Object.freeze(["μm", "nm"]);

const UsedMaterialField = ({ field }) => (
  <span>{field || field === "0" ? field : "taken from library"}</span>
);

function NumMetacellsEditComponent(props) {
  const [touched, setTouched] = useState(false);
  return (
    <NumberInput
      inputProps={{ style: { textAlign: "left", minWidth: 100 } }}
      onChange={event => {
        setTouched(true);
        props.onChange(event.target.value);
      }}
      onBlur={() => setTouched(true)}
      value={props.value || ""}
      error={touched ? props.error : false}
      helperText={touched ? props.helperText : ""}
    />
  );
}

export function IdealComponent({
  isEditing,
  metaCellConfigData,
  setMetaCellConfigData,
  materialsData,
  setMaterialsData,
  incidentLightData,
  setIncidentLightData,
  materials,
  bindSubmitForm,
  bindResetForm
}) {
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedMaterialRowId, setSelectedMaterialRowId] = useState(null);

  const tableRef = useRef(null);
  const tableRefMaterials = useRef(null);

  // Helper functions for tables
  const getSelectOptions = selectedMaterialId => {
    let materialsValues = Object.values(materials.byId);
    let optionSelected = "";
    if (selectedMaterialId && selectedMaterialId !== undefined) {
      optionSelected = materials.byId[selectedMaterialId];
      materialsValues = materialsValues.filter(item => item !== optionSelected);
      materialsValues.unshift(optionSelected);
    }
    return materialsValues;
  };

  const getMaterialName = materialId => {
    if (!materialId) return "";
    const materialsValues = Object.values(materials.byId);
    const material = materialsValues.find(
      material => material?.id === materialId
    );
    if (!material) return "";
    return material.name;
  };

  return (
    <div style={{ textAlign: "left" }}>
      <EnhancedMaterialTable
        title={"Meta cell configuration"}
        tableRef={tableRef}
        selectedEntityId={selectedRowId}
        selectEntity={() => setSelectedRowId(1)}
        onRowKeyDown={event => {
          if (event.key === "Enter") setSelectedRowId(1);
        }}
        options={{
          paging: false,
          search: false,
          sorting: false,
          draggable: false,
          maxBodyHeight: 350,
          actionsColumnIndex: -1
        }}
        columns={[
          {
            title: "Number of metacells",
            field: "num_metacells",
            tooltip:
              "Number of metacells used for evenly spaced phase discretization over a 2pi phase delay. Leave empty to set it as non-discretized.",
            editComponent: NumMetacellsEditComponent,
            validate: rowData =>
              !rowData.num_metacells || /^[0-9]+$/.test(rowData.num_metacells)
                ? ""
                : "Only numbers are allowed"
          },
          {
            title: "Width",
            field: "width",
            editComponent: NumMetacellsEditComponent,
            validate: rowData =>
              !rowData.width ? "This field is required" : ""
          },
          {
            title: "Height",
            field: "height",
            editComponent: NumMetacellsEditComponent,
            validate: rowData =>
              !rowData.height ? "This field is required" : ""
          },
          {
            title: "Unit",
            field: "unit",
            tooltip: "Unit of the meta cell size",
            editComponent: props => (
              <FormControl style={{ width: "100%" }}>
                <UnselfishSelect
                  name="unit"
                  value={props.value || "μm"}
                  onChange={event => {
                    props.onChange(event.target.value);
                  }}
                >
                  {unitOptions.map(unit => (
                    <MenuItem name="UnitOption" value={unit} key={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </UnselfishSelect>
              </FormControl>
            )
          }
        ]}
        data={metaCellConfigData}
        editable={{
          isEditable: rowData => isEditing,
          onRowUpdate: (newData, oldData) =>
            new Promise(resolve => {
              const data = [...metaCellConfigData];
              const index = data.indexOf(oldData);
              data[index] = newData;
              setMetaCellConfigData(data);
              setSelectedRowId(null);
              resolve();
            })
        }}
      />
      <EnhancedMaterialTable
        style={{ marginTop: 15, overflow: "auto" }}
        title={"Materials"}
        tableRef={tableRefMaterials}
        selectedEntityId={selectedMaterialRowId}
        selectEntity={id => setSelectedMaterialRowId(id)}
        onRowKeyDown={event => {
          if (event.key === "Enter") {
            setSelectedMaterialRowId(selectedMaterialRowId);
          }
        }}
        options={{
          paging: false,
          search: false,
          sorting: false,
          draggable: false,
          maxBodyHeight: 350,
          actionsColumnIndex: -1
        }}
        columns={[
          {
            title: "Material type",
            field: "name",
            editable: "never"
          },
          {
            title: "Material",
            field: "material",
            render: rowData => <span>{getMaterialName(rowData.material)}</span>,
            editComponent: props => {
              const { value, onChange } = props;
              const options = getSelectOptions(value).sort((a, b) =>
                a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1
              );
              return (
                <Autocomplete
                  id="material"
                  name="material"
                  options={options}
                  value={value ? materials.byId[value]?.name || "" : ""}
                  initialInputValue={
                    value ? materials.byId[value]?.name || "" : ""
                  }
                  onChange={onChange}
                />
              );
            }
          },
          {
            title: "n",
            field: "refractiveIndex",
            tooltip: "Refractive index (-). Leave empty to use library values",
            render: rowData => (
              <UsedMaterialField field={rowData.refractiveIndex} />
            ),
            editComponent: props => (
              <NumberInput
                inputProps={{
                  style: { textAlign: "left", minWidth: 100 }
                }}
                onChange={event => {
                  props.onChange(event.target.value);
                }}
                value={props.value || ""}
              />
            )
          },
          {
            title: "k",
            field: "absorptionCoeff",
            tooltip:
              "Absorption coefficient (-). Leave empty to use library values",
            render: rowData => (
              <UsedMaterialField field={rowData.absorptionCoeff} />
            ),
            editComponent: props => (
              <NumberInput
                inputProps={{
                  style: { textAlign: "left", minWidth: 100 }
                }}
                onChange={event => {
                  props.onChange(event.target.value);
                }}
                value={props.value || ""}
              />
            )
          }
        ]}
        data={materialsData}
        editable={{
          isEditable: rowData => isEditing,
          onRowUpdate: (newData, oldData) =>
            new Promise(resolve => {
              const data = [...materialsData];
              const index = data.indexOf(oldData);
              data[index] = newData;
              setMaterialsData(data);
              setSelectedMaterialRowId(null);
              resolve();
            })
        }}
      />
      <div style={{ width: "auto", margin: "auto", paddingTop: 15 }}>
        <SetPointForm
          isEditing={isEditing}
          setPoint={incidentLightData}
          bindSubmitForm={bindSubmitForm}
          bindResetForm={bindResetForm}
          onSubmit={newSetPoint => setIncidentLightData(newSetPoint)}
          hideAccuracy={true}
        />
      </div>
    </div>
  );
}
